import {Description, Dialog, DialogBackdrop, DialogPanel, DialogTitle, Transition} from "@headlessui/react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload, faStar, faTimes} from "@fortawesome/free-solid-svg-icons";
import {useBoundStore} from "../../../Store/store";
import toast from "react-hot-toast";
import * as React from "react";
import {useForm} from "react-hook-form";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import {TextAreaInput} from "../../Form/TextAreaInput";
import {SelectInput} from "../../Form/SelectInput";
import {PrimaryButton} from "../../Buttons/PrimaryButton";
import {Fragment, useEffect} from "react";
import {BlackButton} from "../../Buttons/BlackButton";
import {DangerButton} from "../../Buttons/DangerButton";
import {useTranslation} from "react-i18next";

export const RemarkForApplying = (props) => {
    const {
        isOpen,
        onClose,
        onConfirm,
        isSubmitting = false,
        matchingStatuten = [],
    } = props;

    const {t} = useTranslation();
    const needsStatuutChoice = matchingStatuten.length > 1;

    const schema = yup.object({
        remark: yup.string().nullable(),
        applied_as_statuut: needsStatuutChoice
            ? yup.string().oneOf(matchingStatuten).required(t('applications_module.statuut_required'))
            : yup.string().nullable(),
    }).required()

    const {
        register,
        handleSubmit,
        reset,
        control,
        setValue,
        getValues,
        formState: {errors},
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues:{
            remark: null,
            applied_as_statuut: null,
        }
    });

    useEffect(() => {
        reset({
            remark: null,
            applied_as_statuut: matchingStatuten.length === 1 ? matchingStatuten[0] : null,
        })
    }, [isOpen, matchingStatuten.join(',')]);

    const statuutOptions = matchingStatuten.map((value, idx) => ({
        id: idx,
        value,
        label: t(`statuten.${value}`),
    }));

    const onSubmit = async (data) => {
        if (isSubmitting) return;

        try {
            await onConfirm({
                remark: data.remark ?? null,
                applied_as_statuut: data.applied_as_statuut ?? null,
            });
        } catch (error) {
            console.error('Error submitting application', error);
        }
    }

    const handleClose = () => {
        if (isSubmitting) return;
        onClose();
    }

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-999" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50"/>
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto z-999">
                    <div className="flex min-h-full items-center justify-center p-4 text-center z-999">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel
                                className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <Dialog.Title
                                    as="h3"
                                    className="text-lg font-medium leading-6 text-gray-900 flex flex-row justify-between my-auto mb-4"
                                >
                                    <span className={'my-auto'}>{t('shift_application.title')}</span>

                                    <div className="ml-3 flex h-7 items-center">
                                        <button
                                            type="button"
                                            className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                                            onClick={handleClose}
                                        >
                                            <span className="absolute -inset-2.5"/>
                                            <span className="sr-only">{t('general.actions.close')}</span>
                                            <FontAwesomeIcon icon={faTimes} className="h-6 w-6" aria-hidden="true"/>
                                        </button>
                                    </div>

                                </Dialog.Title>

                                <div className="mt-2">
                                    <p className="text-sm text-gray-500">
                                        {t('shift_application.remark_description')}
                                    </p>
                                </div>

                                <form className={'grid grid-cols-1'} onSubmit={handleSubmit(onSubmit)}>
                                    {needsStatuutChoice && (
                                        <div className="mb-2">
                                            <SelectInput
                                                name={'applied_as_statuut'}
                                                control={control}
                                                setValue={setValue}
                                                options={statuutOptions}
                                                currentValue={getValues('applied_as_statuut') ?? null}
                                                translationIndex={'general.fields.main_statuut'}
                                                helperText={t('applications_module.applied_as_statuut_helper')}
                                                errors={errors}
                                            />
                                        </div>
                                    )}
                                    <div className="mb-2">
                                        <TextAreaInput
                                            register={register}
                                            name={'remark'}
                                            translationIndex={'general.fields.comment'}
                                            errors={errors}
                                        />
                                    </div>

                                    <div className="flex flex-row space-x-4 just">
                                        <PrimaryButton
                                            type="submit"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? t('applications_module.sending') : t('general.actions.send')}
                                        </PrimaryButton>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}